// src/components/studio/StudioLayout.tsx
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Send, Heart, Image as ImageIcon, Edit } from "lucide-react";
import { ArchetypeId } from "../../constants/archetypes";
import { ARCHETYPE_THEMES } from "../../types/archetype.types";
import { StepId, StepConfig } from "../../types/studio.types";
import { useResponsive } from "../../hooks/useResponsive";
import { useStepValidation } from "../../hooks/useStepValidation";
import { canNavigateToStep, getStepStatus } from "../../utils/stepUtils";
import { useAuth } from "../../hooks/useAuth";
import { useAppStore } from "../../store/app.store";
import StudioStep from "./StudioStep";
import StudioFooter from "./StudioFooter";
import Step1Recipient from "./steps/Step1Recipient";
import Step2Letter from "./steps/Step2Letter";
import Step3Photo from "./steps/Step3Photo";
import Step4Gift from "./steps/Step4Gift";
import { tiktokPixelService } from "../../services/tiktok-pixel.service";

interface StudioLayoutProps {
  activeStep: StepId;
  setActiveStep: (step: StepId) => void;
  customization: any;
  setCustomization: (c: any) => void;
  archetype: ArchetypeId;
  onSave: () => Promise<any> | any;
  onPayment: () => void;
  createdCard: any;
  isEditing?: boolean;
}

const STEP_COMPONENTS = {
  1: Step1Recipient,
  2: Step2Letter,
  3: Step3Photo,
  4: Step4Gift,
};

const STEP_CONFIGS: StepConfig[] = [
  {
    id: 1,
    title: "Recipient",
    icon: Send,
    requiredFields: ["recipientName", "senderName"],
    validate: (customization) =>
      Boolean(customization.recipientName?.trim() && customization.senderName?.trim()),
  },
  {
    id: 2,
    title: "Letter",
    icon: Heart,
    requiredFields: ["message (min. 10 characters)"],
    validate: (customization) => Boolean(customization.message?.trim().length >= 10),
  },
  {
    id: 3,
    title: "Photo",
    icon: ImageIcon,
    requiredFields: ["recipientImage"],
    validate: (customization) => Boolean(customization.recipientImage),
  },
];

const LOCAL_CREATE_DRAFT_KEY = "lrnsy_local_create_draft";
const PENDING_FINALIZE_KEY = "lrnsy_pending_finalize_after_auth";

export default function StudioLayout({
  activeStep,
  setActiveStep,
  customization,
  setCustomization,
  archetype,
  onSave,
  onPayment,
  createdCard,
  isEditing = false,
}: StudioLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { isAuthenticated } = useAuth();
  const setPendingAuthRedirect = useAppStore((state) => state.setPendingAuthRedirect);

  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [stylesApplied, setStylesApplied] = useState(false);
  const initialCustomizationRef = useRef(customization);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nodeEnv = process.env.NODE_ENV as string;

    if (nodeEnv === "production" && containerRef.current) {
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.opacity = "1";
          containerRef.current.style.visibility = "visible";
          void containerRef.current.offsetHeight;
        }
        setStylesApplied(true);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setStylesApplied(true);
    }
  }, []);

  const persistLocalDraft = useCallback(() => {
    try {
      localStorage.setItem(
        LOCAL_CREATE_DRAFT_KEY,
        JSON.stringify({
          customization,
          activeStep,
          archetype,
          createdCardId: createdCard?.id || null,
          updatedAt: Date.now(),
        })
      );
    } catch (error) {
      console.error("Failed to persist local draft:", error);
    }
  }, [customization, activeStep, archetype, createdCard?.id]);

  const redirectToAuthForFinalize = useCallback(() => {
    persistLocalDraft();

    const returnTo = `${location.pathname}${location.search}`;

    try {
      localStorage.setItem(
        PENDING_FINALIZE_KEY,
        JSON.stringify({
          returnTo,
          archetype,
          activeStep,
          requestedAt: Date.now(),
        })
      );
    } catch (error) {
      console.error("Failed to persist pending finalize:", error);
    }

    setPendingAuthRedirect({
      returnTo,
      step: activeStep,
      reason: "finalize_surprise",
    });

    tiktokPixelService.track(
      "AuthRequired",
      {
        contents: [
          {
            content_id: createdCard?.id || `draft_${archetype}`,
            content_type: "product",
            content_name: "Finalize Surprise Auth Required",
          },
        ],
      },
      `authrequired:finalize:${createdCard?.id || archetype}`
    );

    navigate("/signin", {
      state: {
        returnTo,
        reason: "finalize_surprise",
      },
    });
  }, [
    persistLocalDraft,
    location.pathname,
    location.search,
    archetype,
    activeStep,
    createdCard?.id,
    setPendingAuthRedirect,
    navigate,
  ]);

  const handlePaymentClick = useCallback(() => {
    const value = customization?.includeBouquet ? Number(customization?.bouquetAmount || 0) : 0;

    tiktokPixelService.trackInitiateCheckout(
      {
        content_id: createdCard?.id || `draft_${archetype}`,
        content_type: "product",
        content_name: `${archetype} Experience Checkout`,
      },
      value,
      "KES",
      `initiatecheckout:${createdCard?.id || archetype}`
    );

    onPayment();
  }, [customization, createdCard?.id, archetype, onPayment]);

  const validation = useStepValidation(customization);
  const theme = ARCHETYPE_THEMES[archetype]?.gradient || "from-primary/30 via-black to-black";
  const CurrentStepComponent = STEP_COMPONENTS[activeStep as keyof typeof STEP_COMPONENTS];

  useEffect(() => {
    if (createdCard && initialCustomizationRef.current) {
      const hasChanges =
        JSON.stringify(customization) !== JSON.stringify(initialCustomizationRef.current);
      setHasUnsavedChanges(hasChanges);
    }
  }, [customization, createdCard]);

  const handleNext = useCallback(async () => {
    const isValid = STEP_CONFIGS.find((s) => s.id === activeStep)?.validate(customization);

    if (!isValid && activeStep < 3) {
      alert(`Please complete all required fields in step ${activeStep}`);
      return;
    }

    if (activeStep < 3) {
      setActiveStep((activeStep + 1) as StepId);
      return;
    }

    if (!validation.isStep1Valid || !validation.isStep2Valid || !validation.isStep3Valid) {
      alert("Please complete all required steps before finalizing");
      return;
    }

    if (!isAuthenticated) {
      redirectToAuthForFinalize();
      return;
    }

    setIsSaving(true);
    try {
      await onSave();
      initialCustomizationRef.current = customization;
      setHasUnsavedChanges(false);
    } finally {
      setIsSaving(false);
    }
  }, [
    activeStep,
    validation,
    customization,
    setActiveStep,
    isAuthenticated,
    redirectToAuthForFinalize,
    onSave,
  ]);

  const handleStepClick = useCallback(
    (stepId: StepId) => {
      if (canNavigateToStep(stepId, validation) && stepId !== activeStep) {
        setActiveStep(stepId);
      }
    },
    [validation, activeStep, setActiveStep]
  );

  const showSavedCardFooter = Boolean(createdCard && !hasUnsavedChanges);
  const showEditingFooter = !createdCard || hasUnsavedChanges;

  if (!stylesApplied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Preparing experience...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`min-h-[100dvh] bg-gradient-to-br ${theme} text-white p-4 md:p-10 flex items-center justify-center overflow-hidden relative`}
      style={{ opacity: 1, visibility: "visible" }}
    >
      {isEditing && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[101] bg-primary/20 backdrop-blur-md px-4 py-2 rounded-full border border-primary/30">
          <span className="text-[10px] font-bold uppercase tracking-widest text-primary flex items-center gap-2">
            <Edit size={12} />
            {hasUnsavedChanges ? "Unsaved Changes" : "Editing Draft"}
          </span>
        </div>
      )}

      <div className="relative z-10 w-full max-w-[1200px] flex flex-col md:flex-row gap-4 h-[90vh] md:h-[75vh] items-center justify-center">
        {STEP_CONFIGS.map((step) => {
          const isActive = activeStep === step.id;
          const status = getStepStatus(step.id, validation);
          const canNavigate = status !== "locked";

          return (
            <StudioStep
              key={step.id}
              step={step}
              isActive={isActive}
              status={status}
              canNavigate={canNavigate}
              isMobile={isMobile}
              onClick={() => handleStepClick(step.id)}
            >
              {isActive && (
                <div className="p-6 md:p-8 h-full flex flex-col">
                  <header className="mb-6 flex items-center gap-4">
                    <div className="bg-primary/20 p-2.5 rounded-2xl text-primary">
                      <step.icon size={16} />
                    </div>
                    <div>
                      <p className="text-primary font-bold uppercase tracking-widest text-[8px]">
                        Step 0{step.id}
                      </p>
                      <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter leading-none">
                        {step.title}
                      </h2>
                      {step.requiredFields.length > 0 && (
                        <p className="text-[8px] text-white/40 mt-1">
                          Required: {step.requiredFields.join(", ")}
                        </p>
                      )}
                    </div>
                  </header>

                  <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                    <CurrentStepComponent
                      customization={customization}
                      setCustomization={setCustomization}
                      validation={validation}
                    />
                  </div>

                  <StudioFooter
                    activeStep={activeStep}
                    validation={validation}
                    showSavedCardFooter={showSavedCardFooter}
                    showEditingFooter={showEditingFooter}
                    isSaving={isSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onNext={handleNext}
                    onPreview={() => navigate(`/preview/${createdCard?.id}`)}
                    onPayment={handlePaymentClick}
                  />
                </div>
              )}
            </StudioStep>
          );
        })}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      `}</style>
    </div>
  );
}
